// src/app/slices/ahuSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IAhu,
  IAHUParams,
  ICasing,
  IComponentData,
  IDmount,
  IInternallWall,
  IListItem,
  ISection,
  ISpecialOptions,
  ISpecialOptionsSettings,
  ITunnel,
  ITunnelFloor,
  IVoltages,
  AHUState,
  IAccessory,
  IComponent,
  ITab,
  IRatedVA,
  ISmartListData,
  ISectionAccessory,
  IAccessoryPosition,
  IIdNameValue,
  IRatingDialogData,
  IConstructionClass,
} from '../models';

const initialState: AHUState = {
  tab: 'ahu',
  selected_section: null,
  ahu_params: null,
  ahus: {},
  ahus_certifications: {},
  tunnels: {},
  tunnelsSections: {},
  casings: {},
  floors: {},
  components: [],
  accessories: [],
  voltages: [],
  powered_by: [],
  air_stream: [],
  roofLayers: [],
  roofShapes: [],
  roofSeamDirections: [],
  roofDirections: [],
  roofOrientation: [],
  specialOptions: {},
  specialOptionsSettings: [],
  dmounts: {},
  internalWalls: {},
  ahuComponents: {},
  ahuRatedVa: {},
  smartListData: null,
  smartListQuery: null,
  smartListItemsCount: null,
  selectedFromSmartList: null,
  sectionAccessories: {},
  accessories_position: {},
  accessories_avaible_sections: {},
  ratingDialogData: null,
  independentRatingDialogData: null,
  project_id: null,
  construction_classes: [],
  tunnels_indexes: []
};

const ahuSlice = createSlice({
  name: 'ahu',
  initialState,
  reducers: {
    setTab: (state, action: PayloadAction<ITab>) => {
      state.tab = action.payload;
    },
    setSelectedSection: (state, action: PayloadAction<null | ISection>) => {
      state.selected_section = action.payload;
    },
    setAhuParams: (state, action: PayloadAction<IAHUParams | null>) => {
      state.ahu_params = action.payload;
    },
    setAhu: (state, action: PayloadAction<IAhu>) => {
      if (action.payload.id) state.ahus[action.payload.id] = action.payload;
    },
    setCurrentProjectId: (state, action: PayloadAction<number>) => {
      state.project_id = action.payload;
    },
    setConstructionClasses: (
      state,
      action: PayloadAction<IConstructionClass[]>
    ) => {
      state.construction_classes = action.payload;
    },
    setComponents: (state, action: PayloadAction<IComponent[]>) => {
      state.components = action.payload;
    },
    setAccessories: (state, action: PayloadAction<IAccessory[]>) => {
      state.accessories = action.payload;
    },
    setTunnelsIndexes: (state, action: PayloadAction<IIdNameValue[]>) => {
      state.tunnels_indexes = action.payload;
    },
    setAhuTunnel: (
      state,
      action: PayloadAction<{ tunnel: ITunnel; id: number }>
    ) => {
      const { tunnel, id } = action.payload;
      const index = state.tunnels[id]?.findIndex((t) => t.id === tunnel.id);
      if (index !== undefined && index !== -1) {
        state.tunnels[id][index] = tunnel;
      }
    },
    setAhuTunnels: (
      state,
      action: PayloadAction<{ tunnels: ITunnel[]; id: number }>
    ) => {
      state.tunnels[action.payload.id] = action.payload.tunnels;
    },
    setTunnelCasing: (
      state,
      action: PayloadAction<{ casing: ICasing; id: number }>
    ) => {
      state.casings[action.payload.id] = action.payload.casing;
    },
    setTunnelFloor: (
      state,
      action: PayloadAction<{ floor: ITunnelFloor; id: number }>
    ) => {
      state.floors[action.payload.id] = action.payload.floor;
    },
    setTunnelSections: (
      state,
      action: PayloadAction<{ sections: ISection[]; id: number }>
    ) => {
      state.tunnelsSections[action.payload.id] = action.payload.sections;
    },
    setAhuCertifications: (
      state,
      action: PayloadAction<{ certifications: number[]; id: number }>
    ) => {
      state.ahus_certifications[action.payload.id] =
        action.payload.certifications;
    },
    setVoltages: (state, action: PayloadAction<IVoltages[]>) => {
      state.voltages = action.payload;
    },
    setPowerBy: (state, action: PayloadAction<IIdNameValue[]>) => {
      state.powered_by = action.payload;
    },
    setAirStream: (state, action: PayloadAction<IIdNameValue[]>) => {
      state.air_stream = action.payload;
    },
    setRoofLayers: (state, action: PayloadAction<IListItem[]>) => {
      state.roofLayers = action.payload;
    },
    setRoofShapes: (state, action: PayloadAction<IListItem[]>) => {
      state.roofShapes = action.payload;
    },
    setRoofSeamDirections: (state, action: PayloadAction<IListItem[]>) => {
      state.roofSeamDirections = action.payload;
    },
    setRoofDirections: (state, action: PayloadAction<IListItem[]>) => {
      state.roofDirections = action.payload;
    },
    setRoofOrientation: (state, action: PayloadAction<IListItem[]>) => {
      state.roofOrientation = action.payload;
    },
    setSpecialOptions: (
      state,
      action: PayloadAction<{ options: ISpecialOptions[]; key: string }>
    ) => {
      state.specialOptions[action.payload.key] = action.payload.options;
    },
    setSpecialOptionsSettings: (
      state,
      action: PayloadAction<ISpecialOptionsSettings[]>
    ) => {
      state.specialOptionsSettings = action.payload;
    },
    setSection: (
      state,
      action: PayloadAction<{ section: ISection; id: number }>
    ) => {
      state.tunnelsSections[action.payload.section.tunnel_id].map(
        (sec: ISection) => {
          if (sec.id === action.payload.id) sec = action.payload.section;
          if (state.selected_section?.id === action.payload.id)
            state.selected_section = action.payload.section;
        }
      );
    },
    addSection: (
      state,
      action: PayloadAction<{ section: ISection; tunnel_id: number }>
    ) => {
      if (!state.tunnelsSections[action.payload.tunnel_id]) {
        state.tunnelsSections[action.payload.tunnel_id] = [];
      }
      state.tunnelsSections[action.payload.tunnel_id].push(
        action.payload.section
      );
    },
    setDmounts: (
      state,
      action: PayloadAction<{ dmounts: IDmount[]; section_id: number }>
    ) => {
      state.dmounts[action.payload.section_id] = action.payload.dmounts;
    },
    addDmount: (
      state,
      action: PayloadAction<{ dmount: IDmount; section_id: number }>
    ) => {
      state.dmounts[action.payload.section_id].push(action.payload.dmount);
    },
    setDmount: (
      state,
      action: PayloadAction<{ dmount: IDmount; id: number; section_id: number }>
    ) => {
      state.dmounts[action.payload.section_id] = state.dmounts[
        action.payload.section_id
      ].map((d: IDmount) =>
        d.id === action.payload.id ? action.payload.dmount : d
      );
    },
    setInternalWalls: (
      state,
      action: PayloadAction<{
        internalWalls: IInternallWall[];
        section_id: number;
      }>
    ) => {
      state.internalWalls[action.payload.section_id] =
        action.payload.internalWalls;
    },
    addInternalWall: (
      state,
      action: PayloadAction<{
        internalWall: IInternallWall;
        section_id: number;
      }>
    ) => {
      state.internalWalls[action.payload.section_id].push(
        action.payload.internalWall
      );
    },
    setInternalWall: (
      state,
      action: PayloadAction<{
        internalWall: IInternallWall;
        id: number;
        section_id: number;
      }>
    ) => {
      state.internalWalls[action.payload.section_id] = state.internalWalls[
        action.payload.section_id
      ].map((d: IInternallWall) =>
        d.id === action.payload.id ? action.payload.internalWall : d
      );
    },
    setAhuComponents: (
      state,
      action: PayloadAction<{ [key: string]: IComponentData }>
    ) => {
      state.ahuComponents = action.payload;
    },
    setRatedVA: (state, action: PayloadAction<IRatedVA>) => {
      state.ahuRatedVa[action.payload.ahu_id] = action.payload;
    },
    setSmartListData: (state, action: PayloadAction<ISmartListData | null>) => {
      state.smartListData = action.payload;
      if (action.payload) state.ratingDialogData = null;
    },
    setSmartListQuery: (state, action: PayloadAction<string | null>) => {
      state.smartListQuery = action.payload;
    },
    setSmartListItemsCount: (state, action: PayloadAction<number | null>) => {
      state.smartListItemsCount = action.payload;
    },
    setSelectedFromSmartList: (
      state,
      action: PayloadAction<{ [key: string]: any } | null>
    ) => {
      state.selectedFromSmartList = action.payload;
    },
    setSectionAccessories: (
      state,
      action: PayloadAction<{
        section_id: number;
        accessories: ISectionAccessory[];
      }>
    ) => {
      state.sectionAccessories[action.payload.section_id] =
        action.payload.accessories;
    },
    setAccessoryPosition: (
      state,
      action: PayloadAction<{
        position: IAccessoryPosition;
        id: number;
      }>
    ) => {
      state.accessories_position[action.payload.id] = action.payload.position;
    },
    setAccessoriesAvaibleSections: (
      state,
      action: PayloadAction<{
        [key: string]: { section_type: string }[];
      }>
    ) => {
      state.accessories_avaible_sections = action.payload;
    },
    setRatingDialogData: (
      state,
      action: PayloadAction<IRatingDialogData | null>
    ) => {
      state.ratingDialogData = action.payload;
      if (action.payload) state.smartListData = null;
    },
    removeAhu: (state, action: PayloadAction<number>) => {
      delete state.ahus[action.payload];
    }
  },
});

export const {
  setTab,
  setSelectedSection,
  setAhuParams,
  setAhu,
  setCurrentProjectId,
  setAccessories,
  setComponents,
  setAhuTunnel,
  setAhuTunnels,
  setTunnelCasing,
  setTunnelFloor,
  setAhuCertifications,
  setVoltages,
  setPowerBy,
  setAirStream,
  setRoofLayers,
  setRoofShapes,
  setRoofSeamDirections,
  setRoofDirections,
  setRoofOrientation,
  setSpecialOptions,
  setSpecialOptionsSettings,
  setTunnelSections,
  setSection,
  addSection,
  setDmounts,
  addDmount,
  setDmount,
  setInternalWall,
  setInternalWalls,
  addInternalWall,
  setAhuComponents,
  setRatedVA,
  setSmartListData,
  setSmartListQuery,
  setSelectedFromSmartList,
  setSmartListItemsCount,
  setSectionAccessories,
  setAccessoryPosition,
  setAccessoriesAvaibleSections,
  setRatingDialogData,
  setConstructionClasses,
  setTunnelsIndexes,
  removeAhu
} = ahuSlice.actions;

export default ahuSlice.reducer;
